// Tiny client-side hook around /api/admin/me — returns whether the
// signed-in user has the admin flag. The endpoint is unauthenticated-
// safe (returns {is_admin: false, signed_in: false} when no session),
// so it's safe to call from any mount point.
//
// The result is cached for the lifetime of the page — admin status
// doesn't change mid-session in any flow we have today. If we add a
// "promote me" admin button later, the consumer can call refetch().

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../api/auth-fetch';

interface AdminMeResponse {
  is_admin: boolean;
  signed_in: boolean;
  user_id?: string;
}

let _cache: AdminMeResponse | null = null;
let _inflight: Promise<AdminMeResponse> | null = null;

async function fetchAdminMe(): Promise<AdminMeResponse> {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const res = await apiFetch('/api/admin/me');
      if (!res.ok) {
        // Transient/unauthenticated failure — do not poison the cache.
        return { is_admin: false, signed_in: false };
      }
      const body = (await res.json()) as AdminMeResponse;
      // Only cache signed-in answers: a "not signed in" result fetched on
      // the login screen must not stick for the whole page lifetime, or the
      // admin menu stays hidden after an SPA login until a hard refresh.
      if (body.signed_in) _cache = body;
      return body;
    } catch {
      return { is_admin: false, signed_in: false };
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function useIsAdmin(): { isAdmin: boolean; ready: boolean; refetch: () => void } {
  const [state, setState] = useState<AdminMeResponse | null>(_cache);

  useEffect(() => {
    let cancelled = false;
    fetchAdminMe().then((r) => {
      if (!cancelled) setState(r);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const refetch = useCallback(() => {
    _cache = null;
    fetchAdminMe().then((r) => setState(r));
  }, []);

  return {
    isAdmin: state?.is_admin === true,
    ready: state !== null,
    refetch,
  };
}
